// src/shared/components/AccessControl.jsx

import React from 'react'
import { Navigate, useLocation, Outlet } from 'react-router-dom'
import { useAuth } from '../../features/auth/AuthContext'
import { CircularLoader } from '@dhis2/ui'

export const SUPERUSER_ROLE_ID = 'yrB6vc5Ip3r'

export const STAFF_ROLES_ID = {
  SENIOR_STAFF: 'vdVjNaqxblC',
  TOP_STAFF: 'gtMJNK0QVL7',
  ADMIN: 'R3lFolCMdoi',
  HOD: 'adeqQmxfCLF',
  CEO: 'nLSSLch316I',
};

const Loader = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    }}
  >
    <CircularLoader />
  </div>
)

export const ProtectedRoute = () => {
  const { user, loading, initialLoad } = useAuth()
  const location = useLocation()

  // ✅ Public routes that don't require authentication
  const publicRoutes = ['/login', '/register', '/set-password']
  const isPublicRoute = publicRoutes.includes(location.pathname)

  if (loading || initialLoad) {
    return <Loader />
  }

  // ✅ Allow public routes without authentication
  if (isPublicRoute) {
    return <Outlet />
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <Outlet />
}

export const RegistrationGuard = () => {
  const { registrationComplete, loading, user, initialLoad } = useAuth();
  const location = useLocation();

  console.log('🛡️ RegistrationGuard check - pathname:', location.pathname, 'loading:', loading, 'initialLoad:', initialLoad, 'user:', !!user, 'registrationComplete:', registrationComplete)

  if (loading || initialLoad) {
    console.log('🛡️ RegistrationGuard: Loading, showing loader')
    return <Loader />
  }

  if (!user) {
    console.log('🛡️ RegistrationGuard: No user, redirecting to /login')
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  const userRoleIds = user.authorities || [];

  // Skip guard for superusers
  if (userRoleIds.includes(SUPERUSER_ROLE_ID)) {
    console.log('🛡️ RegistrationGuard: Superuser, allowing access')
    return <Outlet />
  }

  // User with no org assigned: redirect to /organisation if not already there
  if (!registrationComplete && location.pathname !== '/organisation') {
    console.log('🛡️ RegistrationGuard: Registration not complete, redirecting to /organisation')
    return <Navigate to="/organisation" replace />
  }

  // User with org assigned trying to go to /organisation: redirect to dashboard
  if (registrationComplete && location.pathname === '/organisation') {
    console.log('🛡️ RegistrationGuard: Registration complete, redirecting from /organisation to /dashboard')
    return <Navigate to="/dashboard" replace />
  }

  console.log('🛡️ RegistrationGuard: Allowing access to', location.pathname)
  return <Outlet />
}

export const SuperuserOnlyRoute = ({ children }) => {
  const { user, loading, initialLoad } = useAuth()
  const location = useLocation()

  if (loading || initialLoad) {
    return <Loader />
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  const userRoleIds = user.authorities || []

  // Only allow superuser role id
  if (!userRoleIds.includes(SUPERUSER_ROLE_ID)) {
    return <Navigate to="/" replace />
  }

  return children
}

export const SettingsAccessRoute = ({ children }) => {
  const { user, loading, initialLoad } = useAuth();
  const location = useLocation();

  if (loading || initialLoad) {
    return <Loader />
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  const userRoleIds = user.authorities || [];

  // Allow access only to Admin, CEO, or Superuser
  const hasAccess =
    userRoleIds.includes(STAFF_ROLES_ID.ADMIN) ||
    userRoleIds.includes(STAFF_ROLES_ID.CEO) ||
    userRoleIds.includes(SUPERUSER_ROLE_ID);

  if (!hasAccess) {
    // Redirect unauthorized users to dashboard (or home)
    return <Navigate to="/dashboard" replace />
  }

  return children
}